/* eslint-disable @octopusdeploy/custom-portal-rules/no-restricted-imports */
import { css } from "@emotion/css";
import { ActionButton, ActionButtonType, Button, Tooltip } from "@octopusdeploy/design-system-components";
import { CodeCommitIcon } from "@octopusdeploy/design-system-icons";
import { space } from "@octopusdeploy/design-system-tokens";
import type { GitRef, PersistenceSettings } from "@octopusdeploy/octopus-server-client";
import { HasGitPersistenceSettings, toGitBranch, toGitRefShort } from "@octopusdeploy/octopus-server-client";
import * as React from "react";
import { useState } from "react";
import type { CommitMessageWithDetails } from "~/areas/projects/components/VersionControl/CommitMessageWithDetails";
import BusyFromPromise from "~/components/BusyFromPromise";
import { DialogTrigger } from "~/components/Dialog/DialogTrigger";
import type { PermissionCheckProps } from "~/components/PermissionCheck/PermissionCheck";
import CommitDialog from "./CommitDialog";
interface CommitButtonProps {
    busyLabel: string;
    label: string;
    disabled: boolean | undefined;
    gitRef: GitRef;
    canCommitToGitRef: boolean;
    defaultSummary: string;
    commit(): Promise<unknown>;
    commitMessage: CommitMessageWithDetails;
    onCommitMessageChanged: (commitMessage: CommitMessageWithDetails) => void;
    commitButtonAccessibleName: string;
    commitMessageAccessibleName: string;
    commitDetailsAccessibleName: string;
    quickCommitAccessibleName?: string;
    onNewBranchCreating?: (branchName: string) => Promise<void>;
    onInitializing?: (openDialog: () => void) => void;
    hasPermission: PermissionCheckProps;
    hideNewBranchOptions?: boolean;
    sensitiveValueChanged?: boolean;
}
const CommitButton: React.FC<CommitButtonProps> = (props) => {
    const [commitPromise, setCommitPromise] = useState<Promise<unknown> | undefined>(undefined);
    const commit = () => {
        // We need both buttons to de disabled together.
        // Currently, the disabled state is encapsulated in a single ActionButton, so we can't reuse our existing logic and have to re-invent the wheel a little bit here
        const promise = props.commit();
        setCommitPromise(promise);
    };
    return (<DialogTrigger render={({ open, openDialog, closeDialog }) => {
            if (props.onInitializing) {
                props.onInitializing(openDialog);
            }
            return (<>
                        <BusyFromPromise promise={commitPromise}>
                            {(busy: boolean) => {
                    const disabled = props.disabled || busy;
                    const label = busy ? props.busyLabel : props.label;
                    return (<div role="group" className={buttonContainerStyles}>
                                        {props.sensitiveValueChanged && (<Tooltip content="You have made changes to variables which require both saving to the database and committing to version control">
                                                <ActionButton label={label} type={ActionButtonType.Save} disabled={disabled} onClick={openDialog}/>
                                            </Tooltip>)}
                                        {props.sensitiveValueChanged || <ActionButton label={label} type={ActionButtonType.Save} disabled={disabled} onClick={openDialog}/>}
                                        {/*While it would be nicer to use IconButton here, the styling is all wrong here - this is more like an ActionButton with an Icon as its content */}
                                        {/*We should revisit our components to see if we can make this less awkward*/}
                                        <Tooltip content={<>
                                                    {props.canCommitToGitRef ? (<>
                                                            Quick commit to <strong>{toGitRefShort(props.gitRef)}</strong>
                                                        </>) : (<>Cannot commit directly to {props.gitRef}</>)}
                                                </>}>
                                            <Button importance="primary" icon={<CodeCommitIcon size={20}/>} accessibleName={props.quickCommitAccessibleName ?? "Quick commit"} disabled={disabled || !props.canCommitToGitRef} onClick={commit}/>
                                        </Tooltip>
                                    </div>);
                }}
                        </BusyFromPromise>

                        <CommitDialog gitRef={props.gitRef} canCommitToGitRef={props.canCommitToGitRef} defaultSummary={props.defaultSummary} commitMessage={props.commitMessage} onCloseWithoutCommit={closeDialog} open={open} onCommit={() => {
                    commit();
                    closeDialog();
                }} onCommitMessageChanged={props.onCommitMessageChanged} commitMessageAccessibleName={props.commitMessageAccessibleName} commitDetailsAccessibleName={props.commitDetailsAccessibleName} onNewBranchCreating={async (branchName: string) => {
                    if (props.onNewBranchCreating) {
                        await props.onNewBranchCreating(branchName);
                        closeDialog();
                    }
                }} hasPermission={props.hasPermission} hideNewBranchOptions={props.hideNewBranchOptions} sensitiveValueChanged={props.sensitiveValueChanged}/>
                    </>);
        }}/>);
};
CommitButton.displayName = "CommitButton"
const buttonContainerStyles = css({
    display: "inline-flex",
    gap: space[4],
});
export default CommitButton;
export interface GetCommitButtonProps {
    hasPermission: PermissionCheckProps;
    gitRef: GitRef | undefined;
    persistenceSettings: PersistenceSettings;
    canCommitToGitRef: boolean;
    defaultCommitMessage: string;
    commitMessage: CommitMessageWithDetails;
    updateCommitMessage: (commitMessage: CommitMessageWithDetails) => void;
    commitMessageAccessibleName: string;
    commitDetailsAccessibleName: string;
    commitButtonAccessibleName: string;
    quickCommitAccessibleName?: string;
    onNewBranchCreating?: (branchName: string) => Promise<void>;
    onInitializing?: (openDialog: () => void) => void;
    disableNewBranchCreation?: boolean;
    label: string;
    disabled: boolean;
    busyLabel: string;
    onClick: (event: React.MouseEvent | undefined) => Promise<boolean>;
    sensitiveValueChanged?: boolean;
}
export const GetCommitButton: React.FC<GetCommitButtonProps> = (props) => {
    if (!HasGitPersistenceSettings(props.persistenceSettings))
        throw new Error("Config as Code: Trying to access a VCS Property on a non-VCS Project.");
    return (<CommitButton gitRef={props.gitRef ?? toGitBranch(props.persistenceSettings.DefaultBranch)} canCommitToGitRef={props.canCommitToGitRef} label={props.label} disabled={props.disabled} busyLabel={props.busyLabel} defaultSummary={props.defaultCommitMessage} commitMessage={props.commitMessage} onCommitMessageChanged={(commitMessage) => props.updateCommitMessage(commitMessage)} commit={() => props.onClick(undefined)} commitMessageAccessibleName={props.commitMessageAccessibleName} commitDetailsAccessibleName={props.commitDetailsAccessibleName} commitButtonAccessibleName={props.commitButtonAccessibleName} quickCommitAccessibleName={props.quickCommitAccessibleName} onNewBranchCreating={props.onNewBranchCreating} onInitializing={props.onInitializing} hasPermission={props.hasPermission} hideNewBranchOptions={props.disableNewBranchCreation} sensitiveValueChanged={props.sensitiveValueChanged}/>);
};
GetCommitButton.displayName = "GetCommitButton"
